import * as React from 'react';
import classnames from 'classnames';
import ReactResizeDetector from 'react-resize-detector';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { TPAComponentsConsumer } from '../TPAComponentsConfig';
import { ScrollableTabs } from './ScrollableTabs';
import { TabsNavButton } from './TabsNavButton';
import { TABS_DATA_HOOKS, TABS_DATA_KEYS } from './dataHooks';
import { st, classes } from './Tabs.st.css';
import {
  TabsProps,
  TabsState,
  TabsVariant,
  TabsAlignment,
  TabsNavButtonOptions,
  TabsSkin,
  OverflowBehavior,
} from './types';

const SCROLL_EPSILON = 15;

export class Tabs extends React.Component<TabsProps, TabsState> {
  private _tabsRef: ScrollableTabs;
  private _leftButtonRef: TabsNavButton;
  private _rightButtonRef: TabsNavButton;
  private _resizeTimer: NodeJS.Timeout;
  private _indicatorTimer: NodeJS.Timeout;

  static defaultProps = {
    skin: TabsSkin.fullUnderline,
    variant: TabsVariant.fit,
    alignment: TabsAlignment.center,
    isTablist: false,
    overflowBehavior: OverflowBehavior.scroll,
  };

  state = {
    navButtons: TabsNavButtonOptions.none,
    animateIndicator: false,
    selectedTab: this.props.activeTabIndex,
  };

  static getDerivedStateFromProps(props, state) {
    let newState = null;

    if (state.selectedTab !== props.activeTabIndex) {
      newState = {
        navButtons: state.navButtons,
        animateIndicator: props.overflowBehavior === OverflowBehavior.scroll,
      };
    }

    return newState;
  }

  componentDidMount() {
    this._updateButtonsIfNeeded();
  }

  componentDidUpdate() {
    const { selectedTab } = this.state;
    const { activeTabIndex, overflowBehavior } = this.props;

    clearTimeout(this._indicatorTimer);

    if (selectedTab !== activeTabIndex) {
      this.setState(
        {
          selectedTab: activeTabIndex,
          animateIndicator: overflowBehavior === OverflowBehavior.scroll,
        },
        () => {
          this._indicatorTimer = setTimeout(() => {
            this.setState({
              animateIndicator: false,
            });
          }, 350);
        },
      );
    }

    this._updateButtonsIfNeeded();
  }

  componentWillUnmount(): void {
    clearTimeout(this._resizeTimer);
    clearTimeout(this._indicatorTimer);
  }

  _onClickLeft = () => {
    this._tabsRef.scrollLeft();
  };

  _onClickRight = () => {
    this._tabsRef.scrollRight();
  };

  _onClickItem = (index: number, e: React.BaseSyntheticEvent) => {
    const { onTabClick } = this.props;
    onTabClick(index, e);
  };

  _getNewNavButtons() {
    const scrollPosition = this._tabsRef.getNavScrollPosition();
    let newNavButtons = TabsNavButtonOptions.none;

    if (scrollPosition.scrollLeft >= SCROLL_EPSILON) {
      newNavButtons = TabsNavButtonOptions.left;
    }

    if (scrollPosition.scrollRight >= SCROLL_EPSILON) {
      newNavButtons =
        newNavButtons === TabsNavButtonOptions.none
          ? TabsNavButtonOptions.right
          : TabsNavButtonOptions.both;
    }

    return newNavButtons;
  }

  _updateButtonsIfNeeded = () => {
    const { navButtons } = this.state;
    const newNavButtons = this._getNewNavButtons();

    if (newNavButtons !== navButtons) {
      this.setState({ navButtons: newNavButtons });
    }
  };

  _onResize = () => {
    clearTimeout(this._resizeTimer);

    this._updateButtonsIfNeeded();

    this._resizeTimer = setTimeout(() => {
      this._tabsRef.updateIndicatorPosition();
    }, 100);
  };

  _tabsRefCallback = (el: ScrollableTabs) => {
    this._tabsRef = el;
  };

  _leftButtonRefCallback = (el: TabsNavButton) => {
    this._leftButtonRef = el;
  };

  _rightButtonRefCallback = (el: TabsNavButton) => {
    this._rightButtonRef = el;
  };

  _getDataAttributes(mobile, rtl) {
    const { navButtons } = this.state;
    const { skin } = this.props;

    return {
      [TABS_DATA_KEYS.skin]: skin,
      [TABS_DATA_KEYS.mobile]: mobile,
      [TABS_DATA_KEYS.rtl]: rtl,
      [TABS_DATA_KEYS.navButtons]: navButtons,
    };
  }
  render() {
    const { navButtons, animateIndicator, selectedTab } = this.state;
    const {
      items,
      alignment,
      skin,
      variant,
      className,
      overflowBehavior,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      isTablist,
      onKeyDown,
    } = this.props;
    const leftButtonWidth = this._leftButtonRef
      ? this._leftButtonRef.width()
      : 0;
    const rightButtonWidth = this._rightButtonRef
      ? this._rightButtonRef.width()
      : 0;
    return (
      <TPAComponentsConsumer>
        {({ mobile, rtl }) => (
          <ReactResizeDetector handleWidth onResize={this._onResize}>
            {({
              targetRef,
            }: {
              targetRef: React.RefObject<HTMLDivElement>;
            }) => (
              <div
                ref={targetRef}
                className={st(
                  classes.root,
                  {
                    skin,
                    navButtons,
                    mobile,
                    overflowBehavior,
                  },
                  className,
                )}
                {...this._getDataAttributes(mobile, rtl)}
                data-hook={this.props['data-hook']}
                onKeyDown={onKeyDown}
              >
                <div className={st(classes.wrapper)}>
                  <ScrollableTabs
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={ariaDescribedBy}
                    alignment={alignment}
                    variant={variant}
                    items={items}
                    className={classes.navigation}
                    onClickItem={this._onClickItem}
                    onScroll={this._updateButtonsIfNeeded}
                    activeTabIndex={selectedTab}
                    animateIndicator={animateIndicator}
                    ref={this._tabsRefCallback}
                    rtl={rtl}
                    scrollButtons={{
                      left: leftButtonWidth,
                      right: rightButtonWidth,
                    }}
                    overflowBehavior={overflowBehavior}
                    isTablist={isTablist}
                  />
                  <TabsNavButton
                    onClick={this._onClickLeft}
                    className={classnames(classes.navBtn, classes.navBtnLeft)}
                    data-hook={TABS_DATA_HOOKS.leftNavButton}
                    ref={this._rightButtonRefCallback}
                  >
                    <ChevronLeft />
                  </TabsNavButton>
                  <TabsNavButton
                    onClick={this._onClickRight}
                    className={classnames(classes.navBtn, classes.navBtnRight)}
                    data-hook={TABS_DATA_HOOKS.rightNavButton}
                    ref={this._leftButtonRefCallback}
                  >
                    <ChevronRight />
                  </TabsNavButton>
                </div>
              </div>
            )}
          </ReactResizeDetector>
        )}
      </TPAComponentsConsumer>
    );
  }
}
